import type {
  Song,
  Setlist,
  SetlistSection,
  SetlistVisibilityMode,
  SongPoolEntry,
  ErrorEnvelope,
} from '@stage/contracts';
import { ApiError, newRequestId, parseError } from './client.js';

/**
 * "Live show state" API client (M3): repertoire, set list, song pool, and live-state actions
 * (§17, §18, §24). Shares the {@link ApiError}/{@link ErrorEnvelope} contract and bearer-token
 * pattern of the base admin client, and adds optimistic-concurrency handling for the set-list
 * `PUT` (`If-Match` header → {@link VersionConflictError} on a stale write, §17.6).
 */

const BASE = '/v1';

/** Thrown when a set-list `PUT` is rejected because another editor won the race (§17.6). */
export class VersionConflictError extends ApiError {
  constructor(envelope: ErrorEnvelope | null) {
    super(409, envelope);
    this.name = 'VersionConflictError';
  }
}

/** A page of repertoire songs with an opaque forward cursor (§18.1). */
export interface SongsPage {
  items: Song[];
  nextCursor: string | null;
}

/** Fields accepted when creating/updating a song; `title` is required on create. */
export type SongInput = Partial<Song> & { title: string };

export interface CsvImportResult {
  imported: number;
  skipped: number;
}

export interface SongPoolResult {
  entries: SongPoolEntry[];
}

/** Body written to the set-list `PUT` (§17.6). */
export interface SetlistUpdate {
  sections: SetlistSection[];
  visibilityMode: SetlistVisibilityMode;
}

/** Current-item transitions the Performance Console can drive (§17.4, §24.2). */
export type LiveStateActionType =
  | 'start_item'
  | 'complete_item'
  | 'skip_item'
  | 'return_item'
  | 'clear';

export interface LiveStateAction {
  type: LiveStateActionType;
  itemId?: string;
}

/** Patch body for `PATCH /live-state`. All fields optional — send only what changed. */
export interface LiveStateInput {
  customNowPlaying?: string | null;
  breakActive?: boolean;
  action?: LiveStateAction;
}

/** Compact live-state result echoed by `PATCH /live-state` (distinct from the full Gig). */
export interface LiveStateResult {
  gigId: string;
  snapshotVersion: number;
  currentSetlistItemId: string | null;
}

export interface ShowClient {
  listSongs(artistId: string, cursor?: string): Promise<SongsPage>;
  createSong(artistId: string, input: SongInput): Promise<Song>;
  updateSong(artistId: string, songId: string, patch: Partial<Song>): Promise<Song>;
  deleteSong(artistId: string, songId: string): Promise<void>;
  importSongs(artistId: string, csv: string): Promise<CsvImportResult>;
  getSetlist(gigId: string): Promise<Setlist>;
  /** `PUT` with optimistic concurrency: `expectedVersion` is sent as `If-Match` (§17.6). */
  putSetlist(gigId: string, expectedVersion: number, update: SetlistUpdate): Promise<Setlist>;
  getSongPool(gigId: string): Promise<SongPoolResult>;
  putSongPool(gigId: string, entries: SongPoolEntry[]): Promise<SongPoolResult>;
  patchLiveState(gigId: string, input: LiveStateInput): Promise<LiveStateResult>;
}

/**
 * Create a show client bound to a live access-token getter, mirroring
 * {@link createAdminClient}. The getter is read per request so silent refresh is transparent.
 */
export function createShowClient(getAccessToken: () => string | null): ShowClient {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');
    headers.set('x-request-id', newRequestId());
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    const res = await fetch(`${BASE}${path}`, { ...init, headers });
    if (!res.ok) {
      if (res.status === 409) throw new VersionConflictError((await parseError(res)).envelope);
      throw await parseError(res);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  const enc = encodeURIComponent;

  return {
    listSongs(artistId, cursor) {
      const qs = cursor ? `?cursor=${enc(cursor)}` : '';
      return request<SongsPage>(`/artists/${enc(artistId)}/songs${qs}`);
    },
    createSong(artistId, input) {
      return request<Song>(`/artists/${enc(artistId)}/songs`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    updateSong(artistId, songId, patch) {
      return request<Song>(`/artists/${enc(artistId)}/songs/${enc(songId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
    deleteSong(artistId, songId) {
      return request<void>(`/artists/${enc(artistId)}/songs/${enc(songId)}`, { method: 'DELETE' });
    },
    importSongs(artistId, csv) {
      return request<CsvImportResult>(`/artists/${enc(artistId)}/songs/import`, {
        method: 'POST',
        body: JSON.stringify({ csv }),
      });
    },
    getSetlist(gigId) {
      return request<Setlist>(`/gigs/${enc(gigId)}/setlist`);
    },
    putSetlist(gigId, expectedVersion, update) {
      return request<Setlist>(`/gigs/${enc(gigId)}/setlist`, {
        method: 'PUT',
        headers: { 'if-match': String(expectedVersion) },
        body: JSON.stringify(update),
      });
    },
    getSongPool(gigId) {
      return request<SongPoolResult>(`/gigs/${enc(gigId)}/song-pool`);
    },
    putSongPool(gigId, entries) {
      return request<SongPoolResult>(`/gigs/${enc(gigId)}/song-pool`, {
        method: 'PUT',
        body: JSON.stringify({ entries }),
      });
    },
    patchLiveState(gigId, input) {
      return request<LiveStateResult>(`/gigs/${enc(gigId)}/live-state`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
    },
  };
}
